 import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "components/button";
import { Input, TextArea } from "components/input";
import { BsExclamationOctagonFill } from "react-icons/bs";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useModalContext } from "components/modal";
import Switch from "rc-switch";
import { useTranslation } from 'react-i18next';
import ToggleSwitch from "components/toggleSwitch";
import { useSnackbarContext } from "components/snackbar";
import { FaInfoCircle } from "react-icons/fa";
import TooltippedElement from "components/TooltippedElement"; 

const PlanModal = (props) => {

  const { t } = useTranslation();
  const tBase = "views.admin.service_plans.components.PlanModal";
  const tr = (key) => t(`${tBase}.${key}`);

  const { setModal } = useModalContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbarContext();

  const [activeTab, setActiveTab] = useState("details");
  const [showDeleteMsg, setShowDeleteMsg] = useState(false);
  const [planInfo, setPlanInfo] = useState({
    ...(props.data || {}),
    new: false,
  });

  const { data: plansData } = useQuery("plans", async () => {
    return await axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/admin?type=PLANS`)
      .then((res) => res.data)
  });

  useEffect(() => {
    if (props.planId === "new" && props.data) {
      // If we have data passed from duplicate, use it
      setPlanInfo({
        ...props.data,
        new: true,
      });
    } else if (props.planId === "new" && plansData) {
      // Only set default new plan data when plansData is loaded
      setPlanInfo({
        id: nextId(),
        name: null,
        duration: null,
        expiring_soon: 30,
        is_contract: false,
        auto_renew: false,
        archived: false,
        description: "",
        terms_and_conditions: "",
        appointment_types: [],
        start_date_type: "immediately",
        new: true, 
      });
    }
  }, [props.planId, props.data, plansData]); 

  const { data: appointmentTypes } = useQuery("applist", async () => {
    return await axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/admin?type=PLANSAPPLIST`)
      .then((res) => res.data)
  });

  const { data: plans = [] } = useQuery("cusplans", async () => {
    return await axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/admin?type=CUSPLANSLIST`)
      .then((res) =>
        res.data.map((a) => +a.service_planid)
      )
  });

  const updatePlans = useMutation(async(planInfo) => {
    return axios.post(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/admin/plans`,
      {
        type: "EDITPLANS",
        data: planInfo,
      }
    )}, {
      onSuccess: () => { showSnackbar(tr("Plan updated successfully")) },
      onError: () => { showSnackbar(tr("Failed to update plan")) },
      onSettled: () => { queryClient.invalidateQueries() }
  });

  const DeletePlan = useMutation(async(planInfo) => {
    return axios.post(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/admin/plans`,
      {
        type: "REMOVEPLAN",
        data: planInfo,
      }
    )}, {
      onSuccess: () => { showSnackbar(tr("Plan deleted successfully")) },
      onError: () => { showSnackbar(tr("Failed to delete plan")) },
      onSettled: () => { queryClient.invalidateQueries() }
  });

  const ArchivePlan = useMutation(async(planInfo) => {
    return axios.post(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/admin/plans`,
      {
        type: "ARCHIVEPLAN",
        data: planInfo,
      }
    )}, {
      onSuccess: (_data, variables) => { 
        variables.archived ? showSnackbar(tr("Plan archived successfully")) : showSnackbar(tr("Plan unarchived successfully"))
      },
      onError: (error, variables) => {
        variables.archived ? showSnackbar(tr("Failed to archive plan")) : showSnackbar(tr("Failed to unarchive plan"))
      },
      onSettled: () => { queryClient.invalidateQueries() }
  });

  const nextId = () => {
    let highestID = 0;
    // plansData is directly an array, not a query result object
    if (plansData && Array.isArray(plansData)) {
      plansData.forEach((a) => {
        if (+a.id >= highestID) {
          highestID = +a.id;
        }
      });
    }
    const newid = highestID + 1;
    return newid;
  };

  const canDelete = (
    planInfo.archived === true && 
    !plans?.includes(Number(planInfo.id)) && 
    !(planInfo.new)  // Can't delete new plans
  );

  const inputChange = (event) => {
    setPlanInfo({ ...planInfo, [event.target.name]: event.target.value });
  };

  const switchChange = (label, index, id) => {
    const existingAppointmentIndex = planInfo.appointment_types?.findIndex(
      appType => appType.id === id.toString()
    );

    if (existingAppointmentIndex !== -1) {
      const updatedAppointmentTypes = planInfo.appointment_types.filter(
        appType => appType.id !== id.toString()
      );
      
      setPlanInfo(prev => ({
        ...prev,
        appointment_types: updatedAppointmentTypes
      }));
    } else {
      const newAppointmentType = {
        id: id.toString(),
        label: label,
        service_plans_toapptypes: {
          appointment_typeid: id.toString(),
          service_planid: planInfo.id,
          quantity: 1
        }
      };

      setPlanInfo(prev => ({
        ...prev,
        appointment_types: [...(prev.appointment_types || []), newAppointmentType]
      }));
    }
  };

  const quantityChange = (event, appointmentId) => {
    if (event.target != null) {
      const newQuantity = +event.target.value;
      if (newQuantity >= 0) {
        setPlanInfo(prev => ({
          ...prev,
          appointment_types: prev.appointment_types?.map(appType => 
            appType.id === appointmentId 
              ? {
                  ...appType,
                  service_plans_toapptypes: {
                    ...appType.service_plans_toapptypes,
                    quantity: newQuantity
                  }
                }
              : appType
          ) || []
        }));
      }
    }
    event.persist();
  };

  const deletePlanButton = () => {
    if (canDelete) {
      setModal({
        label: tr("Delete Plan") + "#" + planInfo.id,
        component: (
          <>
          <div className=" mb-4">
            <p className="text-lg font-semibold">
              {tr("Are you sure you want to delete this plan?")}  
            <br />
            <span className="text-sm text-gray-500">
              {tr("This action cannot be undone.")}
            </span>
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={async (event) => {
                event.preventDefault();
                DeletePlan.mutate(planInfo);
                setModal();
              }}
            >
              {tr("Delete")}
            </Button>
            <Button onClick={() => setModal()}>
              {tr("Cancel")}
            </Button>
          </div>
          </>
        ),
        props: { shouldCloseOnOverlayClick: false },
      });
    } else {
      setShowDeleteMsg(true);
    }
  };

  const duplicatePlan = () => {
    if (!plansData || !Array.isArray(plansData)) {
      showSnackbar(tr("Please wait for data to load"));
      return;
    }

    const newId = nextId();
    const duplicatedPlanData = {
      ...planInfo,
      id: newId, 
      name: `${planInfo.name} (Copy)`, 
      new: true, 
      archived: false, 
      appointment_types: planInfo.appointment_types?.map(appType => ({
        ...appType,
        service_plans_toapptypes: {
          ...appType.service_plans_toapptypes,
          service_planid: newId 
        }
      })) || []
    };

    setModal({
      label: tr("Add Plan"),
      component: (
        <PlanModal 
          planId="new" 
          data={duplicatedPlanData}
        />
      ),
      props: { 
        shouldCloseOnOverlayClick: true,
        onAfterClose: () => {
          queryClient.invalidateQueries();
        },
      },
    });
  };

  const tabOptions = [
    { id: "details", label: tr("Details") },
    { id: "description", label: tr("Description") },
    { id: "terms", label: tr("Terms and Conditions") },
    { id: "appointments", label: tr("Appointments") },
  ];

  const disabledSave = planInfo.name === null ||  planInfo.name === "" || 
    planInfo.duration === null || planInfo.duration === "" || isNaN(+planInfo.duration) ||
    (planInfo.is_contract && !planInfo.terms_and_conditions);

  return (
    <div className="flex flex-col gap-2 w-[800px] h-[600px] relative overflow-hidden px-2 border-t border-gray-200">
      <div className="text-lg text-primary mt-2">{planInfo.name}</div>
      {/* Archived / Delete Message and Button */}
      {planInfo.archived && (
        <div className="flex justify-between items-center mr-2 -mb-2">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-1">
              <BsExclamationOctagonFill className="text-amber-500" size={20} />
              <div>{tr("This Plan Is Archived")}</div>
            </div>
            {showDeleteMsg && (
              <div className="flex items-center space-x-1">
                <BsExclamationOctagonFill className="text-red-500" size={20} />
                <div>{tr("You must remove this plan from all customers before you may delete it.")}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TABS */}
      <div className="mt-4 flex-1 flex flex-col">
        {/* Tab headers */}
        <div className="flex space-x-4 border-b shadow-sm">
          {tabOptions.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                pb-2
                ${
                  activeTab === tab.id
                    ? "border-b-2 border-blue-500 text-blue-600"
                    : "text-gray-600 hover:text-gray-800"
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === "details" && (
          <div className="py-4 px-2">
            {/* Left column */}
            <div className="space-y-4 flex items-start justify-between">
              <div className="flex flex-col space-y-2">
                <Input
                  name="name"
                  label={tr("Plan Name")}
                  required
                  placeholder={tr("Plan Name")}
                  onChange={inputChange}
                  defaultValue={planInfo.name}
                />
                <Input
                  name="duration"
                  label={tr("Duration of plan (months)")}
                  required
                  placeholder={tr("Duration of plan (months)")}
                  onChange={inputChange}
                  defaultValue={planInfo.duration}
                  type="number"
                  min={0}
                />
                <Input
                  name="expiring_soon"
                  label={tr("Expiring Soon (days)")}
                  placeholder={tr("Expiring Soon (days)")}
                  onChange={inputChange}
                  value={planInfo.expiring_soon}
                  defaultValue={planInfo.expiring_soon || 30}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    const numValue = parseInt(value, 10);
                    
                    // If empty, null, or invalid number, set to 0
                    if (value === "" || isNaN(numValue) || numValue < 0) {
                      setPlanInfo((prev) => ({ ...prev, expiring_soon: 0 }));
                    } else {
                      setPlanInfo((prev) => ({ ...prev, expiring_soon: numValue }));
                    }
                  }}
                  type="number"
                  min={0}
                />
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center space-x-2">
                  <TooltippedElement
                    message={tr("Customer will receive a contract email that requires an acceptance to activate the plan.")}
                    placement="top"
                    element={<FaInfoCircle className="text-blue-500" size={20} />}
                  />
                  <span>{tr("Should this plan be a contract?")}</span>
                  <Switch
                    name="is_contract"
                    checked={planInfo.is_contract}
                    onChange={(e) =>
                      setPlanInfo((prev) => ({ ...prev, is_contract: e }))
                    }
                  />
                </div>

                <div className="flex justify-between items-center space-x-2">
                  <div className="flex items-center space-x-2">
                    <TooltippedElement
                      message={tr("Auto renews at end of plan.")}
                      placement="top"
                      element={<FaInfoCircle className="text-blue-500" size={20} />}
                    />
                    <span className="  justify-start">{tr("Should this plan auto-renew?")}</span>
                  </div>
                  <Switch
                    name="auto_renew"
                    checked={planInfo.auto_renew}
                    onChange={(e) =>
                      setPlanInfo((prev) => ({ ...prev, auto_renew: e }))
                    }
                  />
                </div>

                <div>
                  <div>
                    <span>{tr("Start Date of Plan")}</span>
                  </div>
                  <ToggleSwitch 
                    options={[
                      { value: "immediately", label: tr("Immediately") },
                      { value: "new_year", label: tr("January 1st"), tooltip: `${tr("January 1st")}, ${new Date().getFullYear()}` },
                    ]}
                    defaultValue={planInfo.start_date_type || "immediately"}
                    onChange={(selectedValue) => 
                      setPlanInfo((prev) => ({
                        ...prev,
                        start_date_type: selectedValue, 
                      }))
                    }
                    className="mt-0"
                  />
                </div>
              </div>
            </div>

            <div />
          </div>
        )}

        {activeTab === "description" && (
          <div className="h-full py-4 px-2">
            <TextArea
              name="description"
              label={tr("Description")}
              placeholder={tr("Description")}
              onChange={inputChange}
              defaultValue={planInfo.description}
              className="h-full resize-none"
              style={{ boxSizing: "content-box", height: "calc(100% - 50px)" }}
            />
          </div>
        )}

        {activeTab === "terms" && (
          <div className="h-full py-4 px-2">
            <TextArea
              name="terms_and_conditions"
              label={tr("Terms and Conditions")}
              placeholder={tr("Terms and Conditions")}
              required={planInfo.is_contract}
              onChange={inputChange}
              defaultValue={planInfo.terms_and_conditions}
              className="h-full resize-none"
              style={{ boxSizing: "content-box", height: "calc(100% - 50px)" }}
            />
          </div>
        )}

        {activeTab === "appointments" && (
          <div className="pb-4 pt-6 px-2 space-y-3 overflow-y-auto max-h-[450px]">
            {appointmentTypes.map((app, appindex) => {
              const appointmentType = planInfo.appointment_types?.find(appType => appType.id === app.id);
              const quantity = appointmentType?.service_plans_toapptypes?.quantity ?? 0;
              
              return (
                <div
                  key={app.id}
                  className="flex items-center justify-between bg-white shadow-md border rounded-lg px-4 py-2"
                >
                  <span className="font-medium">{app.label}</span>
                  <div className="flex items-center space-x-3">
                    <Input
                      name="quantity"
                      onChange={(e) => quantityChange(e, app.id)}
                      defaultValue={quantity}
                      maxLength={2}
                      disabled={!(quantity >= 1)}
                      className="w-12 text-center border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    <Switch
                      defaultChecked={quantity >= 1}
                      onChange={() => switchChange(app.label, appindex, +app.id)}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Action buttons always visible below the tabs */}
      <div className="h-[50px] bg-white absolute bottom-0 left-0 right-0 flex justify-between border-t p-2">
        <div className="flex items-center space-x-4">
          {props.planId !== "new" && (
            <Button
            onClick={async (e) => {
              e.preventDefault();
              const newVal = !planInfo.archived;
              setPlanInfo((prev) => ({ ...prev, archived: newVal }));
              ArchivePlan.mutate({ id: planInfo.id, archived: newVal });
            }}
            className="text-red-600"
          >
            {planInfo.archived ? tr("Unarchive Plan") : tr("Archive Plan")}
          </Button>
          )}

          {planInfo.archived && (
            <Button
              onClick={async () => deletePlanButton()}
              onBlur={() => setShowDeleteMsg(false)}
              className="!text-[#ff0000]"
            >
              {tr("Delete Plan")}
            </Button>
          )}

          {props.planId !== "new" && (
            <Button
              onClick={duplicatePlan}
              className="!text-[#008cff]"
              disabled={!plansData || !Array.isArray(plansData)}
            >
              {tr("Duplicate Plan")}
            </Button>
            )}

        </div>
        <Button
          onClick={async (e) => {
            e.preventDefault();
            updatePlans.mutate(planInfo, {
              onSettled: () => {
                setModal();
                queryClient.invalidateQueries();
              },
            });
          }}
          primary
          disabled={disabledSave}
        >
        
          {tr("Save Changes")}
        </Button>
      </div>
    </div>
  );
};


export default PlanModal;